import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Box, Button, Typography, IconButton, TextField } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';
import CustomTable from '@src/components/CustomTable';
import Popup from '@src/components/Popup';
import CategoryManagementApi from '@src/apis/categoryManagement';
import { COLOR } from '@src/styles/color';
import { useDebounce } from '@src/hooks';
import CreateCategoryModal from './CreateCategoryModal';
import DetailCategoryModal from './DetailCategoryModal';
import { StyledCategoryManagement } from './index.style';

const CategoryManagement = ({ appId }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const [searchTerm, setSearchTerm] = useState('');

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [deleteCategory, setDeleteCategory] = useState(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchCategories = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pagination.limit,
        ...(appId && { appId }),
        ...(debouncedSearchTerm && { searchTerm: debouncedSearchTerm }),
      };

      const response = await CategoryManagementApi.getCategories(params);

      if (response?.data) {
        setCategories(response.data || []);
        setPagination({
          ...pagination,
          page,
          total: response.total || 0,
          totalPages: response.totalPages || 0,
        });
      }
    } catch (error) {
      enqueueSnackbar(t('fetchCategoriesError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, page: 1 });
    fetchCategories(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (newPage) => {
    fetchCategories(newPage);
  };

  const handleToggleStatus = async (category) => {
    try {
      const response = await CategoryManagementApi.updateCategoryStatus(
        category.id,
        { isActive: !category.isActive },
      );
      if (response) {
        enqueueSnackbar(
          t(category.isActive ? 'categoryDeactivated' : 'categoryActivated'),
          { variant: 'success' },
        );
        fetchCategories(pagination.page);
      }
    } catch (error) {
      enqueueSnackbar(t('updateStatusError'), { variant: 'error' });
    }
  };

  const handleEdit = (category) => {
    const originalCategory = categories.find((c) => c.id === category.id);
    setSelectedCategory(originalCategory || category);
    setOpenDetailModal(true);
  };

  const handleDelete = (category) => {
    const originalCategory = categories.find((c) => c.id === category.id);
    setDeleteCategory(originalCategory || category);
    setOpenDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await CategoryManagementApi.deleteCategory(
        deleteCategory.id,
      );
      if (response?.status) {
        enqueueSnackbar(t('deleteCategorySuccess'), { variant: 'success' });
        setOpenDeleteModal(false);
        setDeleteCategory(null);
        fetchCategories(pagination.page);
      }
    } catch (error) {
      enqueueSnackbar(t('deleteCategoryError'), { variant: 'error' });
    }
  };

  const handleModalClose = () => {
    setOpenCreateModal(false);
    setOpenDetailModal(false);
    setSelectedCategory(null);
  };

  const handleSuccess = () => {
    fetchCategories(pagination.page);
    handleModalClose();
  };

  useEffect(() => {
    fetchCategories();
  }, [debouncedSearchTerm]);

  const heads = [
    {
      label: t('id'),
      valueName: 'id',
      align: 'left',
    },
    {
      label: t('vietnameseName'),
      valueName: 'vietnameseName',
      align: 'left',
    },
    {
      label: t('englishName'),
      valueName: 'englishName',
      align: 'left',
    },
    {
      label: t('koreanName'),
      valueName: 'koreanName',
      align: 'left',
    },
    {
      label: t('chineseName'),
      valueName: 'chineseName',
      align: 'left',
    },
    {
      label: t('order'),
      valueName: 'order',
      align: 'center',
    },
    {
      label: t('status'),
      valueName: 'status',
      align: 'center',
    },
    {
      label: t('actions'),
      valueName: 'action',
      align: 'center',
    },
  ];

  const formatData = categories.map((category) => ({
    ...category,
    id: category.id?.substring(0, 8) || '--',
    vietnameseName: category.translations?.vi?.name || '--',
    englishName: category.translations?.en?.name || '--',
    koreanName: category.translations?.ko?.name || '--',
    chineseName: category.translations?.zh?.name || '--',
    order: category.order || 0,
    status: (
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          handleToggleStatus(category);
        }}
      >
        <FontAwesomeIcon
          icon={category.isActive ? faToggleOn : faToggleOff}
          color={category.isActive ? COLOR.success.base : COLOR.blueGray[64]}
          size="lg"
        />
      </IconButton>
    ),
    action: (
      <Box display="flex" gap={1} justifyContent="center">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(category);
          }}
        >
          <Edit color="primary" fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(category);
          }}
        >
          <Delete color="error" fontSize="small" />
        </IconButton>
      </Box>
    ),
  }));

  return (
    <StyledCategoryManagement>
      <Box className="header-card">
        <Box className="header-card-title">
          <Typography variant="h3">{t('categoryManagement')}</Typography>
          <Button
            variant="contained"
            onClick={() => setOpenCreateModal(true)}
            startIcon={<Add />}
          >
            {t('createCategory')}
          </Button>
        </Box>

        <Box className="filters" display="flex">
          <TextField
            className="search-input"
            size="small"
            placeholder={t('searchCategories')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <Button variant="outlined" onClick={handleSearch}>
            {t('search')}
          </Button>
        </Box>
      </Box>

      <Box className="body-card">
        <CustomTable
          actions={[]}
          items={formatData}
          heads={heads}
          loading={loading}
          pagination={pagination}
          onChangePagination={handlePageChange}
          disableRowClick
        />
      </Box>

      <CreateCategoryModal
        open={openCreateModal}
        onClose={handleModalClose}
        onSuccess={handleSuccess}
        appId={appId}
      />

      <DetailCategoryModal
        open={openDetailModal}
        category={selectedCategory}
        onClose={handleModalClose}
        onSuccess={handleSuccess}
        appId={appId}
      />

      <Popup
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        title={t('confirmDeleteCategory')}
        content={t('deleteCategoryConfirm', {
          name: deleteCategory?.translations?.vi?.name || deleteCategory?.id,
        })}
        okMessage={t('yes')}
        cancelMessage={t('no')}
        onOk={confirmDelete}
        titleColor={COLOR.warning.base}
      />
    </StyledCategoryManagement>
  );
};

export default CategoryManagement;
